import { Alert, Box, Button, Paper, PasswordInput, Stack, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconFingerprint, IconLock, IconMail } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import z from "zod";
import { PageLoader } from "~/components/page-loader";
import { authClient } from "~/lib/auth-client";
import { logger } from "~/lib/logger";
import classes from "./login.module.css";

const schema = z.object({
  email: z.email({ message: "Invalid email" }),
  password: z.string().min(4, { message: "Password too small" }),
});

export const Route = createFileRoute("/_auth/login")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate({ from: "/" });
  const [error, setError] = useState<string>();
  const { isPending } = authClient.useSession();

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: zod4Resolver(schema),
  });

  async function signIn(values: z.infer<typeof schema>) {
    await authClient.signIn.email(values, {
      onSuccess: async () => {
        await navigate({ to: "/" });
        logger.debug("Sign in successful");
      },
      onError: (error: any) => {
        logger.error(error);
        setError(error.error.message || error.error.statusText);
      },
    });
  }

  if (isPending) {
    return <PageLoader />;
  }

  return (
    <Box className={classes.wrapper}>
      {/* Animated gradient orbs (::before & ::after are on .wrapper) */}
      <div className={classes.orbAccent} />
      <div className={classes.noiseOverlay} />

      <Box className={classes.content}>
        <Paper shadow="xl" p={{ base: "lg", sm: 60 }} radius={24} className={classes.card}>
          <Stack align="center" gap={10} mb={40}>
            <ThemeIcon size={70} radius={70} variant="gradient" gradient={{ from: "blue", to: "cyan" }}>
              <IconFingerprint size={38} stroke={1.5} />
            </ThemeIcon>
            <Title ta="center" order={1} fw={900} fz={32}>
              Welcome Back
            </Title>
            <Text c="dimmed" size="md" ta="center">
              Sign in to your Jasi CRM account
            </Text>
          </Stack>

          <form onSubmit={form.onSubmit(signIn, () => setError(undefined))}>
            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error" mb="xl" variant="light" radius="md">
                {error}
              </Alert>
            )}

            <TextInput
              label="Email"
              placeholder="you@email.com"
              required
              size="lg"
              radius="md"
              mb="md"
              variant="unstyled"
              classNames={{ input: classes.input, label: classes.inputLabel, section: classes.inputSection }}
              {...form.getInputProps("email")}
            />
            <PasswordInput
              label="Password"
              placeholder="your password"
              required
              size="lg"
              radius="md"
              variant="unstyled"
              classNames={{
                input: classes.passwordInput,
                label: classes.inputLabel,
                section: classes.inputSection,
                innerInput: classes.innerInput,
                visibilityToggle: classes.visibilityToggle,
              }}
              {...form.getInputProps("password")}
            />

            <Button fullWidth mt={40} size="lg" radius="md" type="submit" className={classes.submitBtn}>
              Sign in
            </Button>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}
